import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { MagnifyingGlass, Image, VideoCamera, Check, ShieldCheck } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const StockSearchModal = ({ open, onClose, onSelect, initialQuery, mediaType }) => {
  const [query, setQuery] = useState(initialQuery || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(mediaType || 'image');
  const [selectedAsset, setSelectedAsset] = useState(null);

  useEffect(() => {
    if (open && initialQuery) {
      setQuery(initialQuery.split(',')[0]); // Use first part of prompt as search
      handleSearch(initialQuery.split(',')[0]);
    }
  }, [open, initialQuery]);

  const handleSearch = async (searchQuery = query) => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/search-stock`, {
        query: searchQuery,
        media_type: activeTab,
        page: 1
      });
      setResults(res.data.results);
    } catch (err) {
      toast.error('Failed to search stock media');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = () => {
    if (selectedAsset) {
      onSelect(selectedAsset);
      setSelectedAsset(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#171717] border-[#333333] text-white max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Search Stock Media</DialogTitle>
        </DialogHeader>
        
        {/* Search Bar */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <Input
              placeholder="Search for images or videos..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="bg-[#0a0a0a] border-[#333333] text-white pl-10"
              data-testid="stock-search-input"
            />
          </div>
          <Button
            onClick={() => handleSearch()}
            disabled={loading}
            className="bg-[#FF5D01] hover:bg-[#e65400] text-white"
            data-testid="stock-search-btn"
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </div>

        {/* Media Type Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setResults([]); }}>
          <TabsList className="bg-[#0a0a0a] border border-[#333333]">
            <TabsTrigger value="image" className="gap-2 data-[state=active]:bg-[#262626]" data-testid="stock-images-tab">
              <Image size={16} /> Images
            </TabsTrigger>
            <TabsTrigger value="video" className="gap-2 data-[state=active]:bg-[#262626]" data-testid="stock-videos-tab">
              <VideoCamera size={16} /> Videos
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Results Grid */}
        <ScrollArea className="h-[400px] mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#FF5D01] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-20 text-[#A1A1AA]">
              {query ? 'No results found. Try different keywords.' : 'Enter a search term to find stock media.'}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {results.map((asset) => (
                <div
                  key={asset.id}
                  onClick={() => setSelectedAsset(asset)}
                  className={`relative rounded-lg overflow-hidden cursor-pointer border-2 transition-colors ${
                    selectedAsset?.id === asset.id 
                      ? 'border-[#FF5D01]' 
                      : 'border-transparent hover:border-[#333333]'
                  }`}
                  data-testid={`stock-result-${asset.id}`}
                >
                  <div className="aspect-video bg-[#262626]">
                    <img
                      src={asset.thumbnail || asset.url}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                    <div className="flex items-center gap-1 text-xs text-white">
                      <span className="bg-[#262626] px-1.5 py-0.5 rounded">{asset.source}</span>
                      {asset.safe_for_commercial && (
                        <span className="flex items-center gap-0.5 text-[#10B981]">
                          <ShieldCheck size={12} weight="fill" />
                        </span>
                      )}
                    </div>
                  </div>
                  {selectedAsset?.id === asset.id && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-[#FF5D01] rounded-full flex items-center justify-center">
                      <Check size={14} weight="bold" className="text-white" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Selected Asset Info & Actions */}
        {selectedAsset && (
          <div className="mt-4 p-4 bg-[#0a0a0a] border border-[#333333] rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-white font-medium">{selectedAsset.source}</div>
                <div className="text-xs text-[#A1A1AA]">{selectedAsset.license}</div>
                {selectedAsset.attribution && (
                  <div className="text-xs text-[#A1A1AA] mt-1">{selectedAsset.attribution}</div>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setSelectedAsset(null)}
                  className="text-[#A1A1AA] hover:text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSelect}
                  className="bg-[#FF5D01] hover:bg-[#e65400] text-white gap-2"
                  data-testid="select-stock-asset-btn"
                >
                  <Check size={16} weight="bold" />
                  Use This Asset
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
